import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  AlertTriangle, RefreshCw, Search, ArrowUpDown, Building, Calendar,
  Loader2, ChevronUp, ChevronDown, Layers, Clock, CheckCircle2,
  TrendingUp, Target, DollarSign, BarChart3, Shuffle, X, MapPin, Users,
  ArrowRight, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/config';

const fmtDate = (d) => {
  if (!d) return '-';
  const p = d.split('-');
  return p.length >= 3 ? `${p[2]}/${p[1]}` : d;
};
const fmtDateFull = (d) => {
  if (!d) return '-';
  const p = d.split('-');
  return p.length >= 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

const PLATFORM_LABELS = {
  booking: 'Booking', booking_com: 'Booking', BO: 'Booking',
  airbnb: 'Airbnb', AI: 'Airbnb', holiday_world: 'HW', HW: 'HW',
  atraveo: 'Atraveo', AT: 'Atraveo', interhome: 'Interhome',
  altri: 'Altro', other: 'Altro', piattaforma: 'Altro', '': '-'
};
const PLATFORM_COLORS = {
  booking: 'bg-blue-100 text-blue-800', booking_com: 'bg-blue-100 text-blue-800', BO: 'bg-blue-100 text-blue-800',
  airbnb: 'bg-rose-100 text-rose-800', AI: 'bg-rose-100 text-rose-800',
  holiday_world: 'bg-amber-100 text-amber-800', HW: 'bg-amber-100 text-amber-800',
  atraveo: 'bg-teal-100 text-teal-800', AT: 'bg-teal-100 text-teal-800',
  interhome: 'bg-purple-100 text-purple-800',
  altri: 'bg-slate-100 text-slate-800', other: 'bg-slate-100 text-slate-800',
  piattaforma: 'bg-slate-100 text-slate-800', '': 'bg-slate-50 text-slate-500',
};
const TYPE_LABELS = { booking: 'Prenotazione', closure_hw: 'Chiusura' };
const PROXIMITY_LABELS = {
  stesso_comune: 'Stesso Comune', stessa_provincia: 'Stessa Provincia',
  stessa_regione: 'Stessa Regione', altra_regione: 'Altra Regione',
};
const PROXIMITY_COLORS = {
  stesso_comune: 'bg-green-100 text-green-800', stessa_provincia: 'bg-blue-100 text-blue-800',
  stessa_regione: 'bg-amber-100 text-amber-800', altra_regione: 'bg-slate-100 text-slate-600',
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ──── KPI Section ────
function KPISection({ kpi }) {
  if (!kpi) return null;
  const cards = [
    { icon: AlertTriangle, label: 'Totale Rilevate', value: kpi.total_detected, color: 'bg-red-100 text-red-700' },
    { icon: Target, label: 'Attive', value: kpi.active_count, color: 'bg-orange-100 text-orange-700' },
    { icon: CheckCircle2, label: 'Risolte', value: kpi.resolved_count, color: 'bg-green-100 text-green-700' },
    { icon: TrendingUp, label: 'Tasso Risoluzione', value: `${kpi.resolution_rate}%`, color: 'bg-emerald-100 text-emerald-700' },
    { icon: Clock, label: 'Tempo Medio Risoluz.', value: kpi.avg_resolution_days > 0 ? `${kpi.avg_resolution_days}gg` : 'N/A', color: 'bg-sky-100 text-sky-700' },
    { icon: BarChart3, label: 'Tasso Overbooking', value: `${kpi.overlap_rate}%`, color: 'bg-violet-100 text-violet-700' },
    { icon: Layers, label: 'Pren. vs Pren.', value: kpi.booking_vs_booking, color: 'bg-orange-100 text-orange-700' },
    { icon: Calendar, label: 'Pren. vs Chiusura', value: kpi.booking_vs_closure, color: 'bg-amber-100 text-amber-700' },
    { icon: DollarSign, label: 'Impatto Economico', value: `${(kpi.economic_impact || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}`, color: 'bg-rose-100 text-rose-700' },
  ];

  return (
    <div className="space-y-3" data-testid="kpi-section">
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
        {cards.map((c, i) => (
          <Card key={i}>
            <CardContent className="p-3 text-center">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-1 ${c.color}`}>
                <c.icon className="w-4 h-4" />
              </div>
              <p className="text-[10px] text-slate-500 leading-tight">{c.label}</p>
              <p className="text-lg font-bold text-slate-900">{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Top immobili */}
        {kpi.top_properties?.length > 0 && (
          <Card>
            <CardContent className="p-3">
              <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                <Building className="w-3.5 h-3.5" /> Top 10 Immobili
              </h4>
              <div className="space-y-1">
                {kpi.top_properties.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-slate-700 truncate mr-2">{i + 1}. {p.name}</span>
                    <Badge variant="outline" className="text-[10px] shrink-0">{p.count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {/* Top piattaforme */}
        {kpi.top_platforms?.length > 0 && (
          <Card>
            <CardContent className="p-3">
              <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                <BarChart3 className="w-3.5 h-3.5" /> Piattaforme coinvolte
              </h4>
              <div className="space-y-1">
                {kpi.top_platforms.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <Badge className={`text-[10px] ${PLATFORM_COLORS[p.platform] || 'bg-slate-100 text-slate-600'}`}>
                      {PLATFORM_LABELS[p.platform] || p.platform}
                    </Badge>
                    <span className="text-slate-600 font-medium">{p.count} coinvolgimenti</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Trend mensile */}
      {kpi.monthly_trend?.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <h4 className="text-xs font-semibold text-slate-600 mb-2">Trend Mensile</h4>
            <div className="flex items-end gap-1 h-20">
              {kpi.monthly_trend.map((m, i) => {
                const maxVal = Math.max(...kpi.monthly_trend.map(x => Math.max(x.detected, x.resolved)), 1);
                const dH = Math.max((m.detected / maxVal) * 64, 4);
                const rH = Math.max((m.resolved / maxVal) * 64, m.resolved > 0 ? 4 : 0);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${m.month}: ${m.detected} rilevate, ${m.resolved} risolte`}>
                    <div className="flex items-end gap-px w-full justify-center">
                      <div className="bg-red-400 rounded-t" style={{ height: `${dH}px`, width: '45%' }} />
                      <div className="bg-green-400 rounded-t" style={{ height: `${rH}px`, width: '45%' }} />
                    </div>
                    <span className="text-[8px] text-slate-400">{m.month.split('-')[1]}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3 mt-1 justify-center">
              <span className="text-[9px] text-slate-500 flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded-sm inline-block" />Rilevate</span>
              <span className="text-[9px] text-slate-500 flex items-center gap-1"><span className="w-2 h-2 bg-green-400 rounded-sm inline-block" />Risolte</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ──── Alternatives Popup ────
function AlternativesPopup({ overlap, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/booking-overlaps/${overlap.id}/alternatives`, { headers: authHeaders() });
        if (res.ok) setData(await res.json());
        else toast.error('Errore nel caricamento alternative');
      } catch { toast.error('Errore di rete'); }
      finally { setLoading(false); }
    })();
  }, [overlap.id]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-white rounded-xl p-8 flex items-center gap-3" onClick={e => e.stopPropagation()}>
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          <span className="text-sm text-slate-600">Ricerca alternative in corso...</span>
        </div>
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-white rounded-xl p-6 max-w-md" onClick={e => e.stopPropagation()}>
          <p className="text-red-600">{data?.error || 'Errore'}</p>
          <Button onClick={onClose} className="mt-4" variant="outline" size="sm">Chiudi</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="alternatives-popup">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-base flex items-center gap-2">
              <Shuffle className="w-4 h-4" /> Alternative per {data.overlap.property_name}
            </h2>
            <p className="text-slate-300 text-xs mt-0.5">
              {data.overlap.booking_a_code} vs {data.overlap.booking_b_code} &middot; Sovrappos. {fmtDateFull(data.overlap.overlap_start)} - {fmtDateFull(data.overlap.overlap_end)}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-testid="close-alternatives">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-4 space-y-5">
          {/* Immobile sorgente */}
          <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
            <MapPin className="w-3.5 h-3.5" />
            <span>{data.source_property.city}, {data.source_property.province} - {data.source_property.region}</span>
          </div>

          {/* Proposte dirette per ogni prenotazione */}
          {data.proposals?.map((proposal, pi) => (
            <div key={pi} className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-blue-600" />
                Spostare <span className="font-mono text-blue-700">{proposal.booking_code}</span>
                <span className="text-slate-500 font-normal">({proposal.guest_name}, {fmtDate(proposal.check_in)}-{fmtDate(proposal.check_out)}, {proposal.guests_count} ospiti)</span>
              </h3>

              {proposal.direct_alternatives?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold border-b border-slate-200">Immobile</th>
                        <th className="px-3 py-2 text-left font-semibold border-b border-slate-200">Indirizzo</th>
                        <th className="px-3 py-2 text-center font-semibold border-b border-slate-200">Posti</th>
                        <th className="px-3 py-2 text-center font-semibold border-b border-slate-200">Vicinanza</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposal.direct_alternatives.map((alt, ai) => (
                        <tr key={ai} className="hover:bg-green-50/40 border-b border-slate-100">
                          <td className="px-3 py-2 font-medium text-slate-800">{alt.property_name}</td>
                          <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">{alt.city}, {alt.province}</td>
                          <td className="px-3 py-2 text-center">
                            <Badge variant="outline" className="text-[10px]">
                              <Users className="w-3 h-3 mr-1" />{alt.max_guests}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Badge className={`text-[10px] ${PROXIMITY_COLORS[alt.proximity] || ''}`}>
                              {PROXIMITY_LABELS[alt.proximity] || alt.proximity}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic pl-6">Nessun immobile direttamente disponibile per queste date e capienza</p>
              )}
            </div>
          ))}

          {/* Remix proposals */}
          {data.remix_proposals?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 border-t border-slate-200 pt-4">
                <Shuffle className="w-4 h-4 text-purple-600" />
                Remix Possibili
                <span className="text-[10px] text-slate-400 font-normal">(catene di spostamento)</span>
              </h3>

              {data.remix_proposals.map((remix, ri) => (
                <Card key={ri} className="border-purple-200">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center gap-2 text-xs">
                      <Badge className="bg-purple-100 text-purple-800 text-[10px]">
                        Catena {remix.chain_depth} {remix.chain_depth === 1 ? 'spostamento' : 'spostamenti'}
                      </Badge>
                      <span className="text-slate-600">
                        Sposta <span className="font-mono font-medium">{remix.target_booking}</span> in{' '}
                        <span className="font-medium text-purple-700">{remix.destination_property.property_name}</span>
                      </span>
                      <Badge className={`text-[10px] ${PROXIMITY_COLORS[remix.destination_property.proximity] || ''}`}>
                        {PROXIMITY_LABELS[remix.destination_property.proximity] || ''}
                      </Badge>
                    </div>

                    {remix.chain_moves?.length > 0 && (
                      <div className="pl-4 border-l-2 border-purple-200 space-y-1">
                        {remix.chain_moves.map((move, mi) => (
                          <div key={mi} className="text-[11px] text-slate-600 flex items-center gap-1">
                            <ChevronRight className="w-3 h-3 text-purple-400" />
                            Sposta <span className="font-mono font-medium">{move.booking_code}</span>
                            ({fmtDate(move.check_in)}-{fmtDate(move.check_out)})
                            <ArrowRight className="w-3 h-3 text-slate-400" />
                            <span className="font-medium">{move.move_to.property_name}</span>
                            ({move.move_to.city}, {move.move_to.max_guests}p)
                            {move.sub_moves?.map((sub, si) => (
                              <span key={si} className="ml-2 text-purple-500">
                                + sposta {sub.booking_code} in {sub.move_to.property_name}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* No results at all */}
          {data.proposals?.every(p => !p.direct_alternatives?.length) && !data.remix_proposals?.length && (
            <div className="text-center py-8 text-slate-400">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Nessuna alternativa trovata per questa sovrapposizione</p>
              <p className="text-xs mt-1">Potrebbe essere necessario cercare in zone piu distanti o verificare manualmente</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ──── Sort Header ────
function SortHeader({ label, field, currentSort, currentDir, onSort }) {
  const active = currentSort === field;
  return (
    <th className="px-3 py-2.5 text-left font-semibold border-b border-r border-slate-200 cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => onSort(field)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (currentDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
      </span>
    </th>
  );
}

// ──── Overlaps Table ────
function OverlapsTable({ overlaps, sortBy, sortDir, onSort, onAlternative, showResolved = false }) {
  if (!overlaps?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-testid="empty-state">
        <AlertTriangle className="w-8 h-8 mb-2 text-slate-300" />
        <p className="text-sm font-medium">Nessuna sovrapposizione</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="overlaps-table">
        <thead className="bg-slate-50 sticky top-0">
          <tr>
            <SortHeader label="IMMOBILE" field="property_name" currentSort={sortBy} currentDir={sortDir} onSort={onSort} />
            <th className="px-3 py-2.5 text-left font-semibold border-b border-r border-slate-200">PREN. A</th>
            <th className="px-3 py-2.5 text-left font-semibold border-b border-r border-slate-200">OSPITE A</th>
            <th className="px-3 py-2.5 text-center font-semibold border-b border-r border-slate-200">PIATT. A</th>
            <th className="px-3 py-2.5 text-left font-semibold border-b border-r border-slate-200">CONFLITTO</th>
            <th className="px-3 py-2.5 text-left font-semibold border-b border-r border-slate-200">OSPITE B</th>
            <th className="px-3 py-2.5 text-center font-semibold border-b border-r border-slate-200">TIPO</th>
            <SortHeader label="SOVRAPPOS." field="overlap_start" currentSort={sortBy} currentDir={sortDir} onSort={onSort} />
            <SortHeader label={showResolved ? "RISOLTA" : "RILEVATA"} field={showResolved ? "resolved_at" : "first_detected_at"} currentSort={sortBy} currentDir={sortDir} onSort={onSort} />
            {onAlternative && <th className="px-3 py-2.5 text-center font-semibold border-b border-slate-200">AZIONI</th>}
          </tr>
        </thead>
        <tbody>
          {overlaps.map((o) => {
            const isClosure = o.booking_b_type !== 'booking';
            const dateField = showResolved ? o.resolved_at : o.first_detected_at;
            const dateStr = dateField ? new Date(dateField).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';
            const platformA = o.booking_a_platform || '';
            return (
              <tr key={o.overlap_key || o.id} className={`hover:bg-red-50/40 transition-colors border-b border-slate-100 ${o.status === 'resolved' ? 'opacity-60' : ''}`} data-testid={`overlap-row-${o.id}`}>
                <td className="px-3 py-2 border-r border-slate-100 font-medium text-slate-800 max-w-[140px] truncate">{o.property_name || 'N/A'}</td>
                <td className="px-3 py-2 border-r border-slate-100">
                  <span className="font-mono text-[11px] font-medium">{o.booking_a_code}</span>
                  <div className="text-[10px] text-slate-500">{fmtDate(o.booking_a_checkin)} - {fmtDate(o.booking_a_checkout)}</div>
                </td>
                <td className="px-3 py-2 border-r border-slate-100 text-slate-700 max-w-[100px] truncate">{o.booking_a_guest || '-'}</td>
                <td className="px-3 py-2 border-r border-slate-100 text-center">
                  <Badge className={`text-[10px] ${PLATFORM_COLORS[platformA] || 'bg-slate-100 text-slate-600'}`}>{PLATFORM_LABELS[platformA] || platformA || '-'}</Badge>
                </td>
                <td className="px-3 py-2 border-r border-slate-100">
                  <span className={`font-mono text-[11px] font-medium ${isClosure ? 'text-amber-700' : ''}`}>{o.booking_b_code}</span>
                  <div className="text-[10px] text-slate-500">{fmtDate(o.booking_b_checkin)} - {fmtDate(o.booking_b_checkout)}</div>
                </td>
                <td className="px-3 py-2 border-r border-slate-100 text-slate-700 max-w-[100px] truncate">{o.booking_b_guest || '-'}</td>
                <td className="px-3 py-2 border-r border-slate-100 text-center">
                  <Badge className={isClosure ? 'bg-amber-100 text-amber-800 text-[10px]' : 'bg-red-100 text-red-800 text-[10px]'}>
                    {TYPE_LABELS[o.booking_b_type] || o.booking_b_type}
                  </Badge>
                </td>
                <td className="px-3 py-2 border-r border-slate-100 text-red-700 font-semibold whitespace-nowrap">{fmtDate(o.overlap_start)} - {fmtDate(o.overlap_end)}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500 whitespace-nowrap border-r border-slate-100">{dateStr}</td>
                {onAlternative && (
                  <td className="px-3 py-2 text-center">
                    <Button size="sm" variant="outline" className="text-[10px] h-7 px-2 gap-1 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => onAlternative(o)} data-testid={`alt-btn-${o.id}`}>
                      <Shuffle className="w-3 h-3" /> Alternative
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
        {overlaps.length} sovrapposizioni
      </div>
    </div>
  );
}

// ──── Main Component ────
export default function SovrapposizioniManager() {
  const [tab, setTab] = useState('future');
  const [pastSubTab, setPastSubTab] = useState('unresolved');
  const [overlaps, setOverlaps] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [sortBy, setSortBy] = useState('first_detected_at');
  const [sortDir, setSortDir] = useState('desc');
  const [showKpi, setShowKpi] = useState(true);
  const [selectedOverlap, setSelectedOverlap] = useState(null);
  const [kpiYear, setKpiYear] = useState(String(new Date().getFullYear()));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (typeFilter !== 'all') params.append('overlap_type', typeFilter);
      if (platformFilter !== 'all') params.append('platform', platformFilter);
      params.append('sort_by', sortBy);
      params.append('sort_dir', sortDir);

      if (tab === 'future') {
        params.append('status', 'active');
        params.append('time_scope', 'future');
      } else if (tab === 'past') {
        if (pastSubTab === 'resolved') {
          params.append('status', 'resolved');
          params.append('time_scope', 'past');
        } else {
          params.append('status', 'active');
          params.append('time_scope', 'past');
        }
      }

      const res = await fetch(`${API}/api/booking-overlaps?${params}`, { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        setOverlaps(d.overlaps || []);
      }
    } catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  }, [search, typeFilter, platformFilter, sortBy, sortDir, tab, pastSubTab]);

  const fetchKpi = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/booking-overlaps/kpi?year=${kpiYear}`, { headers: authHeaders() });
      if (res.ok) setKpi(await res.json());
    } catch { /* ignore */ }
  }, [kpiYear]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchKpi(); }, [fetchKpi]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API}/api/booking-overlaps/scan`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        const r = await res.json();
        toast.success(`Scansione: ${r.new_count} nuove, ${r.resolved_count} risolte`);
        fetchData();
        fetchKpi();
      }
    } catch { toast.error('Errore nella scansione'); }
    finally { setScanning(false); }
  };

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const platforms = [...new Set(overlaps.flatMap(o => [o.booking_a_platform, o.booking_b_platform].filter(Boolean)))].sort();

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4" data-testid="sovrapposizioni-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Sovrapposizioni Prenotazioni
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Gestione sovrapposizioni con ricerca alternative automatiche</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowKpi(v => !v)} className="gap-1 text-xs" data-testid="toggle-kpi">
              <BarChart3 className="w-3.5 h-3.5" /> {showKpi ? 'Nascondi' : 'Mostra'} KPI
            </Button>
            <Button onClick={handleScan} disabled={scanning} variant="outline" size="sm" className="gap-1 text-xs" data-testid="scan-overlaps-btn">
              {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {scanning ? 'Scansione...' : 'Scansione Manuale'}
            </Button>
          </div>
        </div>

        {/* KPI */}
        {showKpi && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <select value={kpiYear} onChange={e => setKpiYear(e.target.value)} className="h-7 rounded border border-slate-200 px-2 text-xs bg-white" data-testid="kpi-year-select">
                {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <span className="text-[10px] text-slate-400">Anno KPI</span>
            </div>
            <KPISection kpi={kpi} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="main-tabs">
          <button
            onClick={() => setTab('future')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'future' ? 'bg-white text-red-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-future"
          >
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5" />
            Future
          </button>
          <button
            onClick={() => setTab('past')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'past' ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-past"
          >
            <Clock className="w-3.5 h-3.5 inline mr-1.5" />
            Passate
          </button>
        </div>

        {/* Past sub-tabs */}
        {tab === 'past' && (
          <div className="flex gap-1 bg-slate-50 p-0.5 rounded w-fit" data-testid="past-sub-tabs">
            <button
              onClick={() => setPastSubTab('unresolved')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${pastSubTab === 'unresolved' ? 'bg-red-100 text-red-700' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="sub-tab-unresolved"
            >
              Non Risolte
            </button>
            <button
              onClick={() => setPastSubTab('resolved')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${pastSubTab === 'resolved' ? 'bg-green-100 text-green-700' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="sub-tab-resolved"
            >
              Risolte
            </button>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                <Input placeholder="Cerca per codice, ospite, immobile..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-input" />
              </div>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="type-filter">
                <option value="all">Tutti i tipi</option>
                <option value="booking">Prenot. vs Prenot.</option>
                <option value="closure">Prenot. vs Chiusura</option>
              </select>
              <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)} className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="platform-filter">
                <option value="all">Tutte le piattaforme</option>
                {platforms.map(p => <option key={p} value={p}>{PLATFORM_LABELS[p] || p}</option>)}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Caricamento...
              </div>
            ) : (
              <OverlapsTable
                overlaps={overlaps}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                onAlternative={tab === 'future' ? setSelectedOverlap : null}
                showResolved={tab === 'past' && pastSubTab === 'resolved'}
              />
            )}
          </CardContent>
        </Card>

        {/* Alternatives popup */}
        {selectedOverlap && (
          <AlternativesPopup overlap={selectedOverlap} onClose={() => setSelectedOverlap(null)} />
        )}
      </div>
    </Layout>
  );
}
